import React, { useEffect, useState } from 'react';
import { Clock, MapPin, CheckCircle2, User } from 'lucide-react';
import { LoggedInUser } from '../components/LoginScreen';
const INITIAL_EMPLOYEES = [{
  id: '1',
  name: 'John Doe',
  shift: 'Morning',
  time: '06:00 AM - 02:00 PM',
  isOff: false
}, {
  id: '2',
  name: 'Sarah Smith',
  shift: 'Evening',
  time: '02:00 PM - 10:00 PM',
  isOff: false
}, {
  id: '3',
  name: 'Mike Johnson',
  shift: 'Night',
  time: '10:00 PM - 06:00 AM',
  isOff: false
}, {
  id: '4',
  name: 'Emily Davis',
  shift: 'OFF',
  time: '-',
  isOff: true
}];
interface EmployeeAttendanceProps {
  loggedInUser?: LoggedInUser | null;
}
export function EmployeeAttendance({
  loggedInUser
}: EmployeeAttendanceProps) {
  const [employees, setEmployees] = useState(INITIAL_EMPLOYEES);
  const [selectedEmpId, setSelectedEmpId] = useState<string>('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [status, setStatus] = useState<'none' | 'checked-in' | 'checked-out'>('none');
  const [checkInTime, setCheckInTime] = useState<Date | null>(null);
  const [checkOutTime, setCheckOutTime] = useState<Date | null>(null);
  // Initialize with logged in user
  useEffect(() => {
    if (loggedInUser) {
      // Check if user exists in our mock list
      const existingUser = employees.find(e => e.name.toLowerCase() === loggedInUser.name.toLowerCase());
      if (existingUser) {
        setSelectedEmpId(existingUser.id);
      } else {
        // Add the logged in user to the list so they can check in
        const newUser = {
          id: `emp-${Date.now()}`,
          name: loggedInUser.name,
          shift: 'Morning',
          time: '06:00 AM - 02:00 PM',
          isOff: false
        };
        setEmployees(prev => [...prev, newUser]);
        setSelectedEmpId(newUser.id);
      }
    }
  }, [loggedInUser]);
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  const employee = employees.find(e => e.id === selectedEmpId);
  const handleCheckIn = () => {
    setStatus('checked-in');
    setCheckInTime(new Date());
  };
  const handleCheckOut = () => {
    setStatus('checked-out');
    setCheckOutTime(new Date());
  };
  // Calculate live hours
  let workingHours = '0h 0m';
  if (status === 'checked-in' && checkInTime) {
    const diffMs = currentTime.getTime() - checkInTime.getTime();
    const hrs = Math.floor(diffMs / (1000 * 60 * 60));
    const mins = Math.floor(diffMs % (1000 * 60 * 60) / (1000 * 60));
    workingHours = `${hrs}h ${mins}m`;
  } else if (status === 'checked-out' && checkInTime && checkOutTime) {
    const diffMs = checkOutTime.getTime() - checkInTime.getTime();
    const hrs = Math.floor(diffMs / (1000 * 60 * 60));
    const mins = Math.floor(diffMs % (1000 * 60 * 60) / (1000 * 60));
    workingHours = `${hrs}h ${mins}m`;
  }
  // Reset status when changing employee
  const handleEmpChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedEmpId(e.target.value);
    setStatus('none');
    setCheckInTime(null);
    setCheckOutTime(null);
  };
  return <div className="p-6 max-w-4xl mx-auto space-y-6" data-id="element-1285">
      {/* Employee Selector */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-1286">
        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1287">
          Select Employee
        </label>
        <div className="relative" data-id="element-1288">
          <select value={selectedEmpId} onChange={handleEmpChange} className="w-full appearance-none bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-indigo-500 focus:border-indigo-500 block p-3" data-id="element-1289">
            <option value="" data-id="element-1290">-- Select your name --</option>
            {employees.map(emp => <option key={emp.id} value={emp.id} data-id="element-1291">
                {emp.name}
              </option>)}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-1292">
            <User size={18} data-id="element-1293" />
          </div>
        </div>
      </div>

      {employee && <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1294">
          {/* Info Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 flex flex-col items-center text-center" data-id="element-1295">
            <div className="w-24 h-24 bg-indigo-100 rounded-full flex items-center justify-center mb-4 border-4 border-indigo-50" data-id="element-1296">
              <span className="text-3xl font-bold text-indigo-600" data-id="element-1297">
                {employee.name.split(' ').map(n => n[0]).join('')}
              </span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-1298">
              {employee.name}
            </h2>

            <div className="mt-6 w-full space-y-3" data-id="element-1299">
              <div className="bg-gray-50 rounded-lg p-3 flex justify-between items-center border border-gray-100" data-id="element-1300">
                <span className="text-sm text-gray-500" data-id="element-1301">Today's Shift</span>
                <span className={`text-sm font-bold px-2 py-1 rounded ${employee.shift === 'Morning' ? 'bg-green-100 text-green-700' : employee.shift === 'Evening' ? 'bg-blue-100 text-blue-700' : employee.shift === 'Night' ? 'bg-purple-100 text-purple-700' : 'bg-gray-200 text-gray-700'}`} data-id="element-1302">
                  {employee.shift}
                </span>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 flex justify-between items-center border border-gray-100" data-id="element-1303">
                <span className="text-sm text-gray-500" data-id="element-1304">Timing</span>
                <span className="text-sm font-semibold text-gray-800" data-id="element-1305">
                  {employee.time}
                </span>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 flex justify-between items-center border border-gray-100" data-id="element-1306">
                <span className="text-sm text-gray-500" data-id="element-1307">Status</span>
                <span className={`text-sm font-bold flex items-center gap-1 ${status === 'none' ? 'text-gray-500' : status === 'checked-in' ? 'text-green-600' : 'text-blue-600'}`} data-id="element-1308">
                  {status === 'none' ? 'Not Checked In' : status === 'checked-in' ? <>
                      <CheckCircle2 size={16} data-id="element-1309" /> Checked In
                    </> : 'Checked Out'}
                </span>
              </div>
            </div>
          </div>

          {/* Action Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 flex flex-col" data-id="element-1310">
            <div className="text-center mb-8" data-id="element-1311">
              <p className="text-sm font-medium text-gray-500 mb-1" data-id="element-1312">
                Current Time
              </p>
              <h3 className="text-4xl font-bold text-gray-900 font-mono tracking-tight" data-id="element-1313">
                {currentTime.toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit'
            })}
              </h3>
              <p className="text-sm text-gray-500 mt-1" data-id="element-1314">
                {currentTime.toLocaleDateString(undefined, {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
              </p>
            </div>

            {employee.isOff ? <div className="flex-1 flex flex-col items-center justify-center bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 p-6 text-center" data-id="element-1315">
                <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mb-4 text-gray-500" data-id="element-1316">
                  <Clock size={32} data-id="element-1317" />
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2" data-id="element-1318">
                  Enjoy your day off!
                </h3>
                <p className="text-sm text-gray-500" data-id="element-1319">
                  You are not scheduled to work today.
                </p>
              </div> : <div className="flex-1 flex flex-col justify-center space-y-6" data-id="element-1320">
                <button onClick={handleCheckIn} disabled={status !== 'none'} className={`w-full py-5 rounded-xl text-xl font-bold text-white shadow-lg transition-all flex items-center justify-center gap-3 ${status === 'none' ? 'bg-green-500 hover:bg-green-600 hover:-translate-y-1 hover:shadow-green-200' : 'bg-gray-300 cursor-not-allowed shadow-none'}`} data-id="element-1321">
                  <MapPin size={24} data-id="element-1322" />
                  CHECK IN
                </button>

                <button onClick={handleCheckOut} disabled={status !== 'checked-in'} className={`w-full py-5 rounded-xl text-xl font-bold text-white shadow-lg transition-all flex items-center justify-center gap-3 ${status === 'checked-in' ? 'bg-red-500 hover:bg-red-600 hover:-translate-y-1 hover:shadow-red-200' : 'bg-gray-300 cursor-not-allowed shadow-none'}`} data-id="element-1323">
                  <Clock size={24} data-id="element-1324" />
                  CHECK OUT
                </button>

                {status !== 'none' && <div className="bg-indigo-50 rounded-xl p-4 text-center border border-indigo-100" data-id="element-1325">
                    <p className="text-sm text-indigo-600 font-medium mb-1" data-id="element-1326">
                      Working Hours
                    </p>
                    <p className="text-2xl font-bold text-indigo-900 font-mono" data-id="element-1327">
                      {workingHours}
                    </p>
                  </div>}
              </div>}
          </div>
        </div>}
    </div>;
}